// src/components/ChatDMModern.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Spinner, TextInput } from "flowbite-react";
import axios from "../config/axiosInstance";
import { CHAT, USERS } from "../config/apiConfig";
import {
  HiMagnifyingGlass,
  HiPaperAirplane,
  HiArrowLeft,
  HiCheck,
} from "react-icons/hi2";
import { useSocketRef, useSocketReady } from "../contexts/SocketContext";

/* ========================= Utils ========================= */
const presenceToMap = (obj = {}) => {
  const m = new Map();
  Object.entries(obj).forEach(([id, v]) => m.set(String(id), v));
  return m;
};

const cx = (...c) => c.filter(Boolean).join(" ");
const fmtTime = new Intl.DateTimeFormat("pt-BR", {
  hour: "2-digit",
  minute: "2-digit",
});
const fmtDay = (d) =>
  new Intl.DateTimeFormat("pt-BR", { dateStyle: "medium" })
    .format(new Date(d))
    .replace("de ", "");
const isSameDay = (a, b) => {
  const da = new Date(a),
    db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
};
const isNearBottom = (el, px = 140) =>
  el ? el.scrollHeight - el.scrollTop - el.clientHeight < px : false;

/* ========================= Avatar ========================= */
const Avatar = ({ name = "?", url, size = 40, online = false }) => {
  const initials = String(name)
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase())
    .join("");
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      {url ? (
        <img
          src={url}
          alt={name}
          className="h-full w-full rounded-full object-cover ring-1 ring-black/5"
        />
      ) : (
        <div className="h-full w-full rounded-full  text-[#e45412] flex items-center justify-center font-semibold ring-1 ring-black/5">
          {initials || "?"}
        </div>
      )}
      <span
        className={cx(
          "absolute right-0 bottom-0 h-2.5 w-2.5 rounded-full ring-2 ring-white",
          online ? "bg-green-500" : "bg-gray-300"
        )}
      />
    </div>
  );
};

/* ========================= Bolhas ========================= */
const Bubble = ({ meId, m }) => {
  const mine = m.isMine || m.senderId == meId;

  return (
    <div
      className={cx("max-w-[400px] group mb-3", mine ? "ml-auto" : "mr-auto")}
    >
      <div
        className={cx(
          "rounded-2xl px-3 py-2 shadow-md",
          mine
            ? "bg-[#e45412] text-white"
            : "bg-gray-100 text-gray-900 border-gray-200 dark:bg-[#0d1422] dark:border-white/10 dark:text-white/90"
        )}
      >
        {!!m.text && (
          <div className="whitespace-pre-wrap break-words text-[15px] leading-relaxed truncate">
            {m.text}
          </div>
        )}

        <div
          className={cx(
            "mt-1 flex justify-end items-center gap-2 text-[11px] opacity-80",
            mine ? "text-indigo-100" : "text-gray-500"
          )}
        >
          <span>{fmtTime.format(new Date(m.createdAt || Date.now()))}</span>
          {mine && (
            <span className="inline-flex items-center gap-1">
              {m.failed ? (
                <>× falhou</>
              ) : m.isRead ? (
                <>
                  <HiCheck className="-mb-[1px]" /> lida
                </>
              ) : m.isDelivered ? (
                <>
                  <HiCheck className="-mb-[1px]" /> entregue
                </>
              ) : (
                <>• enviando</>
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

/* ========================= Composer ========================= */
const Composer = ({ onSend, onTyping, disabled }) => {
  const [text, setText] = useState("");
  const typingT = useRef(null);

  const send = () => {
    const t = text.trim();
    if (!t) return;
    onSend?.({ text: t, files: [] });
    setText("");
    onTyping?.(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      if (e.altKey) {
        // quebra linha
        return;
      }
      // Enter simples envia
      e.preventDefault();
      send();
    }
  };

  const onChangeText = (e) => {
    setText(e.target.value);
    onTyping?.(true);
    clearTimeout(typingT.current);
    typingT.current = setTimeout(() => onTyping?.(false), 800);
  };

  return (
    <div className="border-t bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60 px-3 py-2">
      <div className="flex items-end gap-2">
        <textarea
          rows={1}
          value={text}
          onChange={onChangeText}
          onKeyDown={onKeyDown}
          placeholder="Escreva uma mensagem…"
          className="flex-1 resize-none rounded-xl border focus:ring-2 focus:ring-[#e45412]/30 focus:border-[#e45412] px-3 py-2 dark:border-white/10 dark:text-white/90"
        />

        <Button
          onClick={send}
          disabled={disabled || !text.trim()}
          className="h-10 bg-[#e45412] hover:bg-[#e45412] focus:ring-2 focus:ring-[#e45412]/40"
        >
          <HiPaperAirplane className="-rotate-45" />
          <span className="ml-1">Enviar</span>
        </Button>
      </div>
      <div className="px-1 pt-1 text-[11px] text-gray-500">
        Enter: enviar • Alt+Enter: nova linha
      </div>
    </div>
  );
};

/* ========================= Separador de data ========================= */
const DaySeparator = ({ day }) => (
  <div className="py-3 flex items-center gap-3">
    <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent via-gray-300 to-transparent dark:via-white/10" />
    <div className="text-[12px] text-gray-500 dark:text-white/60 bg-white dark:bg-[#0b0f1a] px-2 rounded">
      {day}
    </div>
    <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent via-gray-300 to-transparent dark:via-white/10" />
  </div>
);

/* ========================= Componente Principal ========================= */
export default function ChatDMModern() {
  const socketRef = useSocketRef();
  const socketReady = useSocketReady();

  // eu + usuários
  const [me, setMe] = useState(null);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");

  // seleção
  const [activeUser, setActiveUser] = useState(null);
  const [activeDM, setActiveDM] = useState(null);

  // mensagens
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // unread por user
  const [unreadByUser, setUnreadByUser] = useState(new Map());

  // digitação
  const [typing, setTyping] = useState(new Map()); // convId -> Set(userId)

  // scroll/observer
  const listRef = useRef(null);
  const topRef = useRef(null);
  const bottomRef = useRef(null);
  const ioRef = useRef(null);
  const firstLoadRef = useRef(false);
  const lockedRef = useRef(false);
  const autoScrollNextRef = useRef(false); // liga quando deve descer após render
  const lastAppendCameFromMeRef = useRef(false);

  // de-dup e correlação
  const seenIdsRef = useRef(new Set()); // guarda ids reais recebidos
  const pendingByClientIdRef = useRef(new Map()); // clientMsgId -> tempId

  // refs para evitar rebind de handlers
  const activeDMRef = useRef(null);
  const meRef = useRef(null);
  useEffect(() => {
    activeDMRef.current = activeDM;
  }, [activeDM]);
  useEffect(() => {
    meRef.current = me;
  }, [me]);

  /* -------- Ping de áudio para novas mensagens (de outros) -------- */
  const audioCtxRef = useRef(null);
  const playPing = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext ||
          window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.setValueAtTime(880, ctx.currentTime); // A5
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.06, ctx.currentTime + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.18);
      o.connect(g);
      g.connect(ctx.destination);
      o.start();
      o.stop(ctx.currentTime + 0.2);
    } catch {}
  }, []);

  const scrollToBottomSafely = () => {
    const el = listRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    });
  };
  const instantBottom = () => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  };

  const smoothBottom = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
      });
    });
  };

  /* -------- Bootstrap: eu + usuários -------- */
  useEffect(() => {
    (async () => {
      try {
        const meRes = await axios.get(USERS.GET_USER_CURRENT);
        const meData = meRes?.data?.data;
        setMe(meData);

        const uRes = await axios.get(`${USERS.GET_USERS}?itemsPerPage=-1`);
        const list = (
          uRes?.data?.data?.data ||
          uRes?.data?.data ||
          uRes?.data ||
          []
        ).filter((u) => String(u.id) !== String(meData?.id));
        setUsers(list);

        try {
          const ids = list.map((u) => u.id).join(",");
          if (ids.length) {
            const pres = await axios.get(`${CHAT.PRESENCE}?ids=${ids}`);
            const map = pres?.data?.data || {};
            // aplica presence ao users:
            setUsers((prev) =>
              prev.map((u) => {
                const p = map[String(u.id)];
                return p
                  ? { ...u, online: !!p.online, lastSeen: p.lastSeen || null }
                  : u;
              })
            );
          }
        } catch (e) {
          console.warn("Falha ao bootstrap de presença", e);
        }
      } catch (e) {
        console.error("Falha bootstrap", e);
      }
    })();
  }, []);

  /* -------- Bind de eventos do socket -------- */
  useEffect(() => {
    const s = socketRef?.current;
    if (!socketReady || !s) return;

    const ENABLE_LEGACY = false; // evite duplicidade de eventos legacy

    const onNew = ({ conversationId, message }) => {
      // só maneja se for a DM ativa
      if (
        !activeDMRef.current ||
        String(activeDMRef.current.id) !== String(conversationId)
      ) {
        // notificação + badge (apenas se não sou eu mesmo)
        if (
          meRef.current &&
          String(message.senderId) !== String(meRef.current.id)
        ) {
          setUnreadByUser((prev) => {
            const m = new Map(prev);
            const curr = m.get(Number(message.senderId)) || 0;
            m.set(Number(message.senderId), curr + 1);
            return m;
          });
          // ping também quando chega em outra thread
          playPing();
        }
        return;
      }

      const mid = String(message.id || "");
      const cmid = String(message.clientMsgId || "");
      const cameFromMe =
        String(message.senderId ?? "") === String(meRef.current?.id ?? "");

      // merge otimista por clientMsgId
      if (cmid && pendingByClientIdRef.current.has(cmid)) {
        const tempId = pendingByClientIdRef.current.get(cmid);
        pendingByClientIdRef.current.delete(cmid);
        seenIdsRef.current.add(mid);

        setMessages((prev) =>
          prev.map((m) =>
            m.id === tempId
              ? {
                  ...normMsg(message),
                  senderId: message.senderId ?? meRef.current?.id,
                  isDelivered: true,
                }
              : m
          )
        );
      } else {
        if (mid && seenIdsRef.current.has(mid)) return; // de-dup extra
        seenIdsRef.current.add(mid);
        setMessages((prev) => [...prev, normMsg(message)]);
        // som apenas se não for minha
        if (!cameFromMe) playPing();
      }

      // marque para descer APÓS render
      autoScrollNextRef.current = true;
      lastAppendCameFromMeRef.current = cameFromMe;

      // marca leitura
      markRead(conversationId, message.id);
    };

    const onEdited = ({ message }) => {
      setMessages((prev) =>
        prev.map((m) =>
          String(m.id) === String(message.id) ? { ...m, text: message.text } : m
        )
      );
    };

    const onDeleted = ({ messageId, conversationId }) => {
      if (
        activeDMRef.current &&
        String(activeDMRef.current.id) === String(conversationId)
      ) {
        setMessages((prev) =>
          prev.filter((m) => String(m.id) !== String(messageId))
        );
      }
    };

    const onTyping = ({ conversationId, userId, isTyping }) => {
      if (
        !activeDMRef.current ||
        String(activeDMRef.current.id) !== String(conversationId)
      ) {
        return;
      }
      setTyping((prev) => {
        const map = new Map(prev);
        const key = String(conversationId);
        const set = new Set(map.get(key) || []);
        if (isTyping) set.add(String(userId));
        else set.delete(String(userId));
        map.set(key, set);
        return map;
      });
    };

    const onRead = ({ conversationId, readerId, lastReadMessageId }) => {
      if (!activeDMRef.current || activeDMRef.current.id !== conversationId)
        return;
      // só interessa quando o OUTRO usuário leu minhas mensagens
      if (readerId == meRef.current?.id) return;

      setMessages((prev) =>
        prev.map((m) =>
          // minhas mensagens nessa conversa, com id <= lastReadMessageId viram lidas
          m.senderId === meRef.current?.id && m.id <= lastReadMessageId
            ? { ...m, isRead: true }
            : m
        )
      );
    };

    const rejoin = () => {
      if (activeDMRef.current?.id) {
        s.emit("leaveAll");
        s.emit("dm:join", { conversationId: activeDMRef.current.id });
        s.emit("join", { room: `dm:${activeDMRef.current.id}` });
      }
    };

    const onPresence = ({ userId, online, lastSeen }) => {
      setUsers((prev) =>
        prev.map((u) => (u.id == userId ? { ...u, online, lastSeen } : u))
      );
      // se usuário ativo teve presença alterada, reflita no header
      setActiveUser((prev) =>
        prev && prev.id == userId ? { ...prev, online, lastSeen } : prev
      );
    };

    s.on("message:new", onNew);
    s.on("message:edited", onEdited);
    s.on("message:deleted", onDeleted);
    s.on("typing", onTyping);
    s.on("connect", rejoin);
    s.on("reconnect", rejoin);
    s.on("presence:update", onPresence);
    s.on("message:read", onRead);

    if (ENABLE_LEGACY) {
      s.on("dm:message", ({ conversationId, message }) =>
        onNew({ conversationId, message })
      );
      s.on("dm:typing", ({ threadId, userId, isTyping }) =>
        onTyping({ conversationId: threadId, userId, isTyping })
      );
    }

    return () => {
      try {
        s.off("message:new", onNew);
        s.off("message:edited", onEdited);
        s.off("message:deleted", onDeleted);
        s.off("typing", onTyping);
        s.off("connect", rejoin);
        s.off("reconnect", rejoin);
        s.off("presence:update", onPresence);
        s.off("message:read", onRead);
        if (ENABLE_LEGACY) {
          s.off("dm:message");
          s.off("dm:typing");
        }
      } catch {}
    };
  }, [socketRef, socketReady, playPing]);

  // join/leave da sala quando troca a DM
  useEffect(() => {
    const s = socketRef?.current;
    if (!socketReady || !s) return;
    s.emit("leaveAll");
    if (activeDM?.id) {
      s.emit("dm:join", { conversationId: activeDM.id });
      s.emit("join", { room: `dm:${activeDM.id}` });
    }
  }, [socketRef, socketReady, activeDM?.id]);

  /* -------- Abrir DM ao selecionar usuário -------- */
  useEffect(() => {
    if (!activeUser?.id) return;
    openDMWith(activeUser.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUser?.id]);

  // auto-scroll após render quando flag ligada
  useEffect(() => {
    if (!autoScrollNextRef.current) return;
    autoScrollNextRef.current = false;
    smoothBottom();
  }, [messages.length]);

  useEffect(() => {
    const s = socketRef?.current;
    if (!socketReady || !s) return;

    const onPresence = ({ userId, online, lastSeen }) => {
      setUsers((prev) =>
        prev.map((u) =>
          String(u.id) === String(userId)
            ? { ...u, online: !!online, lastSeen: lastSeen || u.lastSeen }
            : u
        )
      );
    };

    const rebootstrap = async () => {
      try {
        // re-sincroniza ao reconectar
        if (!users || users.length === 0) return;
        const ids = users.map((u) => u.id).join(",");
        if (!ids) return;
        const pres = await axios.get(`${CHAT.PRESENCE}?ids=${ids}`);
        const map = pres?.data?.data || {};
        setUsers((prev) =>
          prev.map((u) => {
            const p = map[String(u.id)];
            return p
              ? { ...u, online: !!p.online, lastSeen: p.lastSeen || null }
              : u;
          })
        );
      } catch (e) {
        console.warn("Falha rebootstrap presence", e);
      }
    };

    s.on("presence:update", onPresence);
    s.on("reconnect", rebootstrap);
    s.on("connect", rebootstrap);

    return () => {
      try {
        s.off("presence:update", onPresence);
        s.off("reconnect", rebootstrap);
        s.off("connect", rebootstrap);
      } catch {}
    };
  }, [socketRef, socketReady, users]);

  const openDMWith = async (userId) => {
    try {
      const res = await axios.post(CHAT.DM_ENSURE, { userId: Number(userId) });
      const conv = res?.data?.data?.conversation || res?.data?.data;
      if (!conv?.id) return;

      setActiveDM(conv);

      // limpa estado de de-dup ao trocar de thread
      seenIdsRef.current = new Set();
      pendingByClientIdRef.current = new Map();

      // entra nas salas
      const s = socketRef?.current;
      if (socketReady && s) {
        s.emit("leaveAll");
        s.emit("dm:join", { conversationId: conv.id });
        s.emit("join", { room: `dm:${conv.id}` });
      }

      setUnreadByUser((prev) => {
        const m = new Map(prev);
        m.set(Number(userId), 0);
        return m;
      });

      // reset
      setMessages([]);
      setHasMore(true);
      firstLoadRef.current = false;
      detachObserver();

      await loadMessages(conv.id);
      attachObserver();
      firstLoadRef.current = true;
      scrollToBottomSafely();
    } catch (e) {
      console.error("Erro ensure/open DM", e);
    }
  };

  /* -------- Observer para scroll infinito -------- */
  const attachObserver = useCallback(() => {
    const root = listRef.current;
    const sent = topRef.current;
    if (!root || !sent) return;
    detachObserver();

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          if (!firstLoadRef.current) return;
          if (lockedRef.current) return;
          if (!hasMore) return;
          if (root.scrollTop > 8) return;

          lockedRef.current = true;
          const first = messages[0];
          loadMessages(activeDMRef.current?.id, first?.id).finally(() => {
            lockedRef.current = false;
          });
        });
      },
      { root, threshold: 1 }
    );
    io.observe(sent);
    ioRef.current = io;
  }, [hasMore, messages]);

  // depois de setUsers(list), faça o bootstrap de presença
  const applyPresenceToUsers = useCallback(async (rawUsers) => {
    try {
      const ids = rawUsers.map((u) => u.id).join(",");
      if (!ids) return;
      const pres = await axios.get(`${CHAT.PRESENCE}?ids=${ids}`);
      const map = pres?.data?.data || {};
      setUsers((prev) =>
        prev.map((u) => {
          const p = map[String(u.id)];
          return p ? { ...u, online: !!p.online, lastSeen: p.lastSeen } : u;
        })
      );
    } catch {}
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const meRes = await axios.get(USERS.GET_USER_CURRENT);
        const meData = meRes?.data?.data;
        setMe(meData);

        const uRes = await axios.get(`${USERS.GET_USERS}?itemsPerPage=-1`);
        const list = (
          uRes?.data?.data?.data ||
          uRes?.data?.data ||
          uRes?.data ||
          []
        ).filter((u) => String(u.id) !== String(meData?.id));
        setUsers(list);
        // PRESENCE bootstrap
        await applyPresenceToUsers(list);
      } catch (e) {
        console.error("Falha bootstrap", e);
      }
    })();
  }, [applyPresenceToUsers]);

  const detachObserver = () => {
    ioRef.current?.disconnect?.();
    ioRef.current = null;
  };

  /* -------- Helpers -------- */
  const normMsg = (m) => ({
    id: m.id,
    clientMsgId: m.clientMsgId || undefined,
    text: m.text ?? m.body ?? "",
    senderId: m.senderId,
    createdAt: m.createdAt || Date.now(),
    editedAt: m.editedAt,
    isDelivered: m.isDelivered ?? true,
    isRead: m.isRead ?? false,
    attachments: Array.isArray(m.attachments)
      ? m.attachments.map((a) => ({ id: a.id, url: a.url, name: a.name }))
      : [],
  });

  const loadMessages = async (conversationId, beforeId) => {
    if (!conversationId) return;
    try {
      setLoading(true);
      const res = await axios.get(CHAT.DM_MESSAGES(conversationId), {
        params: { beforeId, limit: 30 },
      });
      const data = Array.isArray(res?.data?.data) ? res.data.data : [];
      const n = data.map(normMsg).sort((a, b) => a.createdAt - b.createdAt);

      // marca ids já vistos para evitar duplicar quando socket reenviar últimas
      n.forEach((m) => m.id && seenIdsRef.current.add(String(m.id)));

      setMessages((prev) => (beforeId ? [...n, ...prev] : [...prev, ...n]));
      setHasMore(res?.data?.hasMore ?? data.length === 30);

      if (!beforeId && n.length) {
        await markRead(conversationId, n[n.length - 1].id);
        requestAnimationFrame(() => requestAnimationFrame(instantBottom));
      }
    } catch (e) {
      console.error("Erro loadMessages", e);
    } finally {
      setLoading(false);
    }
  };

  const markRead = async (conversationId, lastReadMessageId) => {
    try {
      await axios.post(CHAT.DM_READ(conversationId), {
        lastReadMessageId,
      });
      setMessages((prev) => prev.map((m) => ({ ...m, isRead: true })));
    } catch {}
  };

  /* -------- Enviar mensagem -------- */
  const onTyping = (isTyping) => {
    const s = socketRef?.current;
    if (!socketReady || !s || !activeDM?.id) return;
    s.emit("typing", { conversationId: activeDM.id, isTyping });
  };

  const onSend = async ({ text, files }) => {
    if (!activeDM?.id) return;
    const atBottom = isNearBottom(listRef.current);

    // gera um clientMsgId para correlacionar com socket
    const clientMsgId = `c_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2)}`;
    const tempId = `t_${Date.now()}`;

    pendingByClientIdRef.current.set(clientMsgId, tempId);

    const optimistic = normMsg({
      id: tempId,
      clientMsgId,
      text,
      senderId: meRef.current?.id,
      isMine: true,
      createdAt: Date.now(),
      attachments: (files || []).map((f, i) => ({
        id: `temp_${i}`,
        url: URL.createObjectURL(f),
        name: f.name,
      })),
      isDelivered: false,
      isRead: false,
    });

    // NÃO marca como visto ainda; aguarda socket/read
    setMessages((prev) => [...prev, optimistic]);
    autoScrollNextRef.current = true;
    lastAppendCameFromMeRef.current = true;
    instantBottom(); // mantém o instantâneo; o effect também garantirá

    try {
      const res = await axios.post(CHAT.DM_SEND, {
        text,
        conversationId: activeDM.id,
        clientMsgId,
      });

      const real = normMsg(res?.data?.data || {});
      if (real.id) seenIdsRef.current.add(String(real.id));

      setMessages((prev) =>
        prev.map((m) =>
          m.id === tempId
            ? {
                ...real,
                senderId: real.senderId ?? meRef.current?.id,
                isDelivered: true,
              }
            : m
        )
      );

      if (atBottom) smoothBottom();
    } catch (e) {
      console.error(e);
      setMessages((prev) =>
        prev.map((m) => (m.id === tempId ? { ...m, failed: true } : m))
      );
      pendingByClientIdRef.current.delete(clientMsgId);
    }
  };

  /* -------- Filtros / memo -------- */
  const filteredUsers = useMemo(() => {
    const term = q.trim().toLowerCase();
    let list = users;
    if (term) {
      list = users.filter(
        (u) =>
          String(u.name || "")
            .toLowerCase()
            .includes(term) ||
          String(u.email || "")
            .toLowerCase()
            .includes(term)
      );
    }
    return [...list].sort(
      (a, b) =>
        Number(!!b.online) - Number(!!a.online) || a.name.localeCompare(b.name)
    );
  }, [users, q]);

  const typingText = useMemo(() => {
    const set = typing.get(String(activeDM?.id)) || new Set();
    return set.size > 0
      ? "digitando…"
      : activeUser?.online
      ? "online"
      : "offline";
  }, [typing, activeDM?.id, activeUser?.online]);

  /* ========================= Render ========================= */
  return (
    <div className="h-[calc(100vh-120px)] rounded-2xl border border-gray-200 dark:border_white/10 overflow-hidden grid grid-cols-1 md:grid-cols-4 bg-white dark:bg-[#0b0f1a]">
      {/* Sidebar */}
      <div className="border-r border-gray-200 dark:border-white/10 p-3 flex flex-col">
        <div className="mb-3">
          <div className="relative">
            <HiMagnifyingGlass className="absolute left-3 top-2.5 text-gray-400" />
            <TextInput
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nome ou e-mail"
              className="pl-9"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto space-y-2 pr-1">
          {filteredUsers.length === 0 && (
            <div className="text-center text-gray-500 dark:text-white/60 text-sm py-10">
              Nenhum usuário encontrado
            </div>
          )}
          {filteredUsers.map((u) => (
            <button
              key={u.id}
              onClick={() => setActiveUser(u)}
              className={cx(
                "w-full text-left px-3 py-2 rounded-xl border flex items-center gap-3 transition",
                String(activeUser?.id) === String(u.id)
                  ? "bg-gray-200"
                  : "bg-white border-gray-200 hover:bg-gray-50 dark:bg-[#0b0f1a] dark:border-white/10 dark:hover:bg-white/5"
              )}
            >
              <Avatar name={u.name} url={u.avatar} online={!!u.online} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium truncate dark:text-white/90">
                    {u.name}
                  </div>
                </div>
                <div className="text-[12px] text-gray-500 dark:text-white/60 truncate">
                  {u.email}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Chat */}
      <section className="flex flex-col md:col-span-3">
        {/* Header */}
        <header className="px-4 py-3 border-b border-gray-200 dark:border-white/10 bg-white/80 dark:bg-[#0d1422]/80 backdrop-blur">
          {activeUser ? (
            <div className="flex items-center gap-3">
              <button
                className="md:hidden rounded-lg border border-gray-200 dark:border-white/10 p-2"
                onClick={() => setActiveUser(null)}
              >
                <HiArrowLeft />
              </button>
              <Avatar
                name={activeUser.name}
                url={activeUser.avatar}
                online={!!activeUser.online}
              />
              <div className="min-w-0">
                <div className="font-semibold truncate dark:text-white">
                  {activeUser.name}
                </div>
                <div className="text-[12px] text-gray-500 dark:text-white/60">
                  {typingText}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-600 dark:text-white/70">
              Selecione um usuário para começar
            </div>
          )}
        </header>

        {/* Mensagens – UM ÚNICO SCROLL CONTAINER */}
        <main
          ref={listRef}
          className="flex-1 overflow-y-auto px-4 py-3 bg-gradient-to-b from-gray-50 to-white dark:from-[#0d1422] dark:to-[#0b0f1a]"
          style={{ maxHeight: "70vh", minHeight: "70vh" }}
        >
          <div ref={topRef} />
          {loading && (
            <div className="flex items-center justify-center py-3">
              <Spinner size="sm" />
            </div>
          )}

          {messages.length === 0 && activeUser && !loading && (
            <div className="text-center text-gray-500 dark:text-white/60 text-sm py-10">
              Sem mensagens ainda. Diga oi! 👋
            </div>
          )}

          {messages.length > 0 &&
            (() => {
              const out = [];
              let lastDay = null;
              messages.forEach((m, idx) => {
                const day = fmtDay(m.createdAt);
                if (!lastDay || !isSameDay(m.createdAt, lastDay)) {
                  out.push(<DaySeparator key={`sep_${idx}`} day={day} />);
                  lastDay = m.createdAt;
                }
                out.push(<Bubble key={m.id} meId={me?.id} m={m} />);
              });
              return out;
            })()}
          <div ref={bottomRef} />
        </main>

        {/* Composer */}
        {activeDM && (
          <Composer
            onSend={onSend}
            onTyping={onTyping}
            disabled={!activeDM || !meRef.current?.id}
          />
        )}
      </section>
    </div>
  );
}
